
import { useState, useRef } from 'react';

export interface AudioRecording {
  blob: Blob;
  url: string;
  startTime: Date;
  duration: number;
  id: string;
}

export const useRecordingManager = () => {
  const [recordings, setRecordings] = useState<AudioRecording[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<Date | null>(null);
  
  // Initialize a new media recorder
  const initializeRecorder = (stream: MediaStream) => {
    try {
      // Initialize the MediaRecorder with the stream
      mediaRecorderRef.current = new MediaRecorder(stream);
      
      audioChunksRef.current = [];
      startTimeRef.current = new Date();
      
      // Set up event handlers for the MediaRecorder
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        if (audioChunksRef.current.length === 0) {
          console.log("No audio data captured");
          return;
        }
        
        try {
          // Create a blob from the chunks
          const originalBlob = new Blob(audioChunksRef.current, { 
            type: mediaRecorderRef.current?.mimeType || 'audio/webm' 
          });
          
          // Create URL for the blob
          const audioUrl = URL.createObjectURL(originalBlob);
          
          if (startTimeRef.current) {
            const duration = Math.floor((new Date().getTime() - startTimeRef.current.getTime()) / 1000);
            
            setRecordings(prev => [
              ...prev, 
              { 
                blob: originalBlob, 
                url: audioUrl, 
                startTime: startTimeRef.current as Date, 
                duration: duration,
                id: `rec-${Date.now()}-${Math.floor(Math.random() * 1000)}`
              }
            ]);
          }
        } catch (error) {
          console.error("Error processing recording:", error);
          setErrorMessage(`Error processing recording: ${error instanceof Error ? error.message : String(error)}`);
        }
      };
      
      return true;
    } catch (err) {
      console.error("Error initializing recorder:", err);
      setErrorMessage(`Could not initialize recorder: ${err instanceof Error ? err.message : String(err)}`);
      return false;
    }
  };
  
  // Start recording
  const startRecording = () => {
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.start(1000); // Capture data in 1-second chunks
        return true;
      } catch (err) {
        console.error("Error starting recording:", err);
        setErrorMessage(`Could not start recording: ${err instanceof Error ? err.message : String(err)}`);
        return false;
      }
    }
    return false;
  };
  
  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log("Stopping recording...");
      
      try {
        mediaRecorderRef.current.stop();
        return true;
      } catch (err) {
        console.error("Error stopping recording:", err);
        setErrorMessage(`Error stopping recording: ${err instanceof Error ? err.message : String(err)}`);
        return false;
      }
    }
    return false;
  };
  
  // Clear all recordings
  const clearRecordings = () => {
    // Clean up any object URLs to prevent memory leaks
    recordings.forEach(recording => {
      URL.revokeObjectURL(recording.url);
    });
    setRecordings([]);
  };
  
  // Delete a single recording by ID
  const deleteRecording = (id: string) => {
    // Find the recording to revoke its URL
    const recordingToDelete = recordings.find(rec => rec.id === id);
    if (recordingToDelete) {
      URL.revokeObjectURL(recordingToDelete.url);
    }
    
    // Remove it from the recordings array
    setRecordings(prev => prev.filter(rec => rec.id !== id));
  };
  
  // Download the recording as a file
  const downloadRecording = (recording: AudioRecording, filename = 'recording.wav') => {
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = recording.url;
    a.download = filename;
    a.click();
    
    // Cleanup
    document.body.removeChild(a);
  };
  
  return {
    recordings,
    errorMessage,
    mediaRecorderRef,
    initializeRecorder,
    startRecording,
    stopRecording,
    clearRecordings,
    deleteRecording,
    downloadRecording,
    setErrorMessage
  };
};
